import { Calculator, UIUtil } from '@suntex.core/animation';

enum MeterMode {
  FILL_RIGHT = 0,
  FILL_LEFT = 1,
  FILL_UP = 2,
  FILL_DOWN = 3,
  FILL_OUT_HORIZONTALLY = 4,
  FILL_OUT_VERTICALLY = 5,
}

export class Meter {
  static readonly FILL_RIGHT = MeterMode.FILL_RIGHT;
  static readonly FILL_LEFT = MeterMode.FILL_LEFT;
  static readonly FILL_UP = MeterMode.FILL_UP;
  static readonly FILL_DOWN = MeterMode.FILL_DOWN;
  static readonly FILL_OUT_HORIZONTALLY = MeterMode.FILL_OUT_HORIZONTALLY;
  static readonly FILL_OUT_VERTICALLY = MeterMode.FILL_OUT_VERTICALLY;

  public fill: createjs.DisplayObject & { nominalBounds?: createjs.Rectangle };
  public max: number;
  public animMs: number;
  public mode: MeterMode;
  public textField?: createjs.Text;
  public displayAsPercent: boolean = false;

  constructor(
    fill: createjs.DisplayObject,
    max: number = 1.0,
    animMs = 500,
    mode = 0,
  ) {
    this.fill = fill;
    this.max = max;
    this.animMs = animMs;
    this.mode = mode;

    this.setMode(mode);
  }

  setMax(max: number): void {
    this.max = max;
  }

  setMode(mode: MeterMode): void {
    this.mode = mode;

    const bounds =
      // Despite the type definition, `getBounds()` can return null
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      this.fill.getBounds() ??
      this.fill.nominalBounds ??
      new createjs.Rectangle(0, 0, 0, 0);

    switch (mode) {
      case Meter.FILL_RIGHT:
        UIUtil.setRegistrationPoint(this.fill, 0, bounds.height / 2);
        break;
      case Meter.FILL_DOWN:
        UIUtil.setRegistrationPoint(this.fill, bounds.width / 2, 0);
        break;
      case Meter.FILL_LEFT:
        UIUtil.setRegistrationPoint(this.fill, bounds.width, bounds.height / 2);
        break;
      case Meter.FILL_UP:
        UIUtil.setRegistrationPoint(this.fill, bounds.width / 2, bounds.height);
        break;
      case Meter.FILL_OUT_HORIZONTALLY:
      case Meter.FILL_OUT_VERTICALLY:
        UIUtil.setRegistrationPoint(
          this.fill,
          bounds.width / 2,
          bounds.height / 2,
        );
        break;
    }

    if (
      this.mode == Meter.FILL_RIGHT ||
      this.mode == Meter.FILL_LEFT ||
      this.mode == Meter.FILL_OUT_HORIZONTALLY
    ) {
      this.fill.scaleX = 0.0;
    } else {
      this.fill.scaleY = 0.0;
    }
  }

  bindTextField(textField: createjs.Text): void {
    this.textField = textField;
  }

  update(value: number, animate = true): number {
    value = Calculator.bound(value, 0, this.max);

    if (
      this.mode == Meter.FILL_RIGHT ||
      this.mode == Meter.FILL_LEFT ||
      this.mode == Meter.FILL_OUT_HORIZONTALLY
    ) {
      UIUtil.tween(
        this.fill,
        'scaleX',
        value / this.max,
        animate ? this.animMs : 0,
      );
    } else {
      UIUtil.tween(
        this.fill,
        'scaleY',
        value / this.max,
        animate ? this.animMs : 0,
      );
    }

    if (this.textField != null) {
      if (this.displayAsPercent) {
        // eslint-disable-next-line no-magic-numbers
        this.textField.text = '' + Calculator.round(value * 100, 0) + '%';
      } else {
        this.textField.text = '' + value;
      }
    }

    return value;
  }
}
